import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useCart } from "@/hooks/useCart";
import type { CartItem, Product } from "../types";

export interface CartContextValue {
  cart: CartItem[];
  cartLoaded: boolean;
  cartCnt: number;
  cartTot: number;
  addToCart: (p: Product, q?: number) => void;
  updateQty: (id: number, qty: number) => void;
  removeFromCart: (id: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

interface ProviderProps {
  userId: string | null;
  products: Product[];
  children: React.ReactNode;
}

export function CartProvider({ userId, products, children }: ProviderProps) {
  const { cart, setCart, cartLoaded, addToCart, updateQty, removeFromCart, clearCart } =
    useCart(userId, products);

  const cartCnt = useMemo(
    () => (cart as CartItem[]).reduce((s, i) => s + i.qty, 0),
    [cart],
  );
  const cartTot = useMemo(
    () => (cart as CartItem[]).reduce((s, i) => s + i.pr * i.qty, 0),
    [cart],
  );

  // E2E test hook: allow Playwright specs to inject arbitrary cart contents
  // (e.g. an out-of-stock item) without depending on the static catalog or
  // walking the full add-to-cart UI. Exposed only in dev/test builds so it
  // never ships to production.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!import.meta.env.DEV) return;
    (window as any).__nm_e2e_setCart = (items: CartItem[]) => setCart(items as any);
    (window as any).__nm_e2e_clearCart = () => setCart([]);
    // Expose minimal product info so specs that depend on real catalog ids
    // (e.g. NutriCoach add-all flow) can build mocked AI responses against
    // products that actually resolve in the UI.
    (window as any).__nm_e2e_getProducts = () =>
      (products || []).map((p: any) => ({ id: p.id, nm: p.nm, pr: p.pr }));
    return () => {
      try {
        delete (window as any).__nm_e2e_setCart;
        delete (window as any).__nm_e2e_clearCart;
        delete (window as any).__nm_e2e_getProducts;
      } catch {}
    };
  }, [setCart, products]);

  // E2E sync signal: publish a deterministic signature of the cart on every
  // commit so Playwright specs can `waitForFunction` instead of racing React
  // renders after `__nm_e2e_setCart`. Dev/test builds only.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!import.meta.env.DEV) return;
    const sig = (cart as CartItem[])
      .map((i: any) => `${i.id}:${i.qty}:${i.stk ?? ""}:${i.low_stock_threshold ?? ""}`)
      .join("|");
    document.body.dataset.nmCartSig = sig;
    (window as any).__nm_e2e_cartSig = sig;
  }, [cart]);

  const value: CartContextValue = {
    cart: cart as CartItem[],
    cartLoaded,
    cartCnt,
    cartTot,
    addToCart,
    updateQty,
    removeFromCart,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCartCtx(): CartContextValue {
  const v = useContext(CartContext);
  if (!v) throw new Error("useCartCtx must be used inside <CartProvider>");
  return v;
}